import { useEffect, useMemo } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  SESSION_CHECK_RETRY_LABEL,
  SESSION_UNAVAILABLE_DESCRIPTION,
  SESSION_UNAVAILABLE_TITLE
} from '../constants/authSession'
import { useAuthContext } from '../contexts/AuthContext'
import { useRequestsContext } from '../contexts/RequestsContext'
import { buildReturnPathState, resolvePostLoginPath } from '../utils/returnPath'

function AuthGuardPending(): ReactElement {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Проверка сессии...</Typography>
      </Box>
    </Box>
  )
}

function SessionCheckUnavailable(): ReactElement {
  const { sessionCheckErrorMessage, isSessionCheckRetrying, retrySessionCheck } = useAuthContext()

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, textAlign: 'center' }}>
        <Typography variant="h5" component="h1">{SESSION_UNAVAILABLE_TITLE}</Typography>
        <Typography color="text.secondary">{SESSION_UNAVAILABLE_DESCRIPTION}</Typography>
        {sessionCheckErrorMessage && (
          <Typography variant="body2" color="text.secondary">{sessionCheckErrorMessage}</Typography>
        )}
        <Button
          variant="contained"
          disabled={isSessionCheckRetrying}
          startIcon={isSessionCheckRetrying ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => retrySessionCheck()}
        >
          {SESSION_CHECK_RETRY_LABEL}
        </Button>
      </Stack>
    </Box>
  )
}

export function RequireAuth(): ReactElement {
  const { pathname, search, state } = useLocation()
  const { isAuthMetaLoaded, isAuthenticated, signedOutByUser, isSessionCheckFailed } = useAuthContext()
  const returnPathState = useMemo(
    () => (signedOutByUser ? undefined : buildReturnPathState({ pathname, search, state })),
    [pathname, search, signedOutByUser, state]
  )
  if (isSessionCheckFailed) {
    return <SessionCheckUnavailable />
  }

  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return isAuthenticated
    ? <Outlet />
    : <Navigate to="/login" replace state={returnPathState} />
}

export function RequireAnonymous(): ReactElement {
  const location = useLocation()
  const { isAuthMetaLoaded, isAuthenticated, requirePasswordChange, isSessionCheckFailed } = useAuthContext()
  if (isSessionCheckFailed) {
    return <SessionCheckUnavailable />
  }

  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return isAuthenticated
    ? <Navigate to={resolvePostLoginPath(location.state, requirePasswordChange)} replace />
    : <Outlet />
}

interface RequirePasswordChangeResolvedProps {
  children?: ReactNode
}

export function RequirePasswordChangeResolved({ children }: RequirePasswordChangeResolvedProps): ReactElement {
  const { requirePasswordChange } = useAuthContext()
  if (requirePasswordChange) {
    return <Navigate to="/profile" replace />
  }

  return children ? <>{children}</> : <Outlet />
}

export function RequireAdmin(): ReactElement {
  const { isAuthMetaLoaded, hasPermission } = useAuthContext()
  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return hasPermission('SystemAdmin') ? <Outlet /> : <Navigate to="/" replace />
}

export function RequireCatalogAccess(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthMetaLoaded, hasPermission } = useAuthContext()
  const { setStatus } = useRequestsContext()

  const hasAccess = hasPermission('NomenclatureManage')

  useEffect(() => {
    if (!isAuthMetaLoaded) return
    if (hasAccess) return

    setStatus({ kind: 'error', message: 'Недостаточно прав для доступа к каталогу.' })
    navigate('/requests', { replace: true, state: { from: location.pathname } })
  }, [hasAccess, isAuthMetaLoaded, location.pathname, navigate, setStatus])

  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return hasAccess ? <Outlet /> : <AuthGuardPending />
}

export function RequireWarehouseAccess(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthMetaLoaded, hasPermission } = useAuthContext()
  const { setStatus } = useRequestsContext()

  const hasAccess = hasPermission('WarehouseViewStock')

  useEffect(() => {
    if (!isAuthMetaLoaded) return
    if (hasAccess) return

    setStatus({ kind: 'error', message: 'Недостаточно прав для доступа к складу.' })
    navigate('/requests', { replace: true, state: { from: location.pathname } })
  }, [hasAccess, isAuthMetaLoaded, location.pathname, navigate, setStatus])

  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return hasAccess ? <Outlet /> : <AuthGuardPending />
}

export function RequireMetrologyAccess(): ReactElement {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthMetaLoaded, hasPermission } = useAuthContext()
  const { setStatus } = useRequestsContext()

  const hasAccess = hasPermission('MetrologyView')

  useEffect(() => {
    if (!isAuthMetaLoaded) return
    if (hasAccess) return

    setStatus({ kind: 'error', message: 'Недостаточно прав для доступа к мерительному инструменту.' })
    navigate('/requests', { replace: true, state: { from: location.pathname } })
  }, [hasAccess, isAuthMetaLoaded, location.pathname, navigate, setStatus])

  if (!isAuthMetaLoaded) {
    return <AuthGuardPending />
  }

  return hasAccess ? <Outlet /> : <AuthGuardPending />
}
